import React, { useEffect, useRef, useState } from 'react';
import { BaileyStill } from '../bailey/BaileyAvatar';
import { Icon } from '../BaileyBits';
import { hapticError, hapticSuccess, hapticTick } from '../../../_utils/haptics';
import SoulCards from './SoulCards';
import PhonePanel from './PhonePanel';
import VoicePanel, { VoiceLine } from './VoicePanel';
import AnswerPanel, { COMMON_QUESTIONS } from './AnswerPanel';
import { compileSoul, fromPreset, chatGreeting } from './soul';
import { park, readParked, prettyNumber, errorText } from './api';

/**
 * Putting Bailey on the front desk for the first time: three short steps,
 * one question each. Soul, then where to answer, then what Bailey should
 * know. Not now parks it; the home shows a row to finish later.
 */
const KNOWS = ['Your services and prices', 'Opening hours and free times', 'Your team and who does what', 'Your cancellation policy'];

export default function FrontDeskSetup({ fd, look, salonName, motionPaused, onDone, onNotNow }) {
    // Read fresh here, not from the home's copy, so leaving by the back
    // button and coming straight back carries on where they were.
    const [parked] = useState(() => readParked() || {});
    const s = fd.settings;
    // A soul set on the soul page already is kept unless they pick a card here.
    const hadSoul = !!String(s.greeting || '').trim();
    const [step, setStep] = useState(Number(parked.step) || 1);
    const [soul, setSoul] = useState(parked.soul || (hadSoul ? fd.soul.preset : null) || 'warm');
    const [picked, setPicked] = useState(!!parked.picked);
    const [chat, setChat] = useState(parked.chat == null ? true : !!parked.chat);
    const [phone, setPhone] = useState(!!parked.phone);
    const [panel, setPanel] = useState(null);     // 'phone' | 'voice' | { add, question } | { answer }
    const [busy, setBusy] = useState('');
    const [error, setError] = useState('');
    const alive = useRef(true);
    useEffect(() => () => { alive.current = false; }, []);
    const price = s.voicePricePerMinute != null ? `Calls cost ${s.voicePricePerMinute}p a minute, only while I’m answering.` : 'Calls are charged per minute, only while I’m answering.';
    // What Go live writes, and what the preview says: the card they picked
    // here (keeping their own notes), or the soul they already have.
    const keepSoul = hadSoul && !picked;
    const soulNow = keepSoul ? fd.soul : { ...fromPreset(soul), notes: hadSoul ? fd.soul.notes : '' };

    // Leaving part-way keeps the place; a parked switch-on shows on the home.
    // Only once they've done something: just opening this page isn't starting it.
    const touched = useRef(false);
    useEffect(() => {
        if (!touched.current) { touched.current = true; return; }
        park({ step, soul, picked, chat, phone });
        fd.bump();
    }, [step, soul, picked, chat, phone]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { if (phone && !s.voiceNumber) setPhone(false); }, [phone, s.voiceNumber]);

    const pickSoul = (id) => { setSoul(id); setPicked(true); };
    const notNow = () => { hapticTick(); park({ step, soul, picked, chat, phone }); fd.bump(); onNotNow(); };

    const goLive = async () => {
        setBusy('live'); setError('');
        try {
            await fd.save({ enabled: chat, voiceEnabled: phone && !!s.voiceNumber, ...(keepSoul ? {} : { greeting: compileSoul(soulNow) }) });
            park(null);
            fd.bump();
            hapticSuccess();
            // Left while it saved: stay where they went.
            if (alive.current) onDone();
        } catch (e) { if (!alive.current) return; hapticError(); setError(errorText(e, 'That didn’t save. Try again.')); setBusy(''); }
    };

    const canGo = chat || (phone && !!s.voiceNumber);
    const answered = (q) => fd.answers.find((r) => String(r.question || '').trim().toLowerCase() === q.toLowerCase());

    return (
        <div className="bly-home fd-setup">
            <div className="fd-steps" role="progressbar" aria-valuemin={1} aria-valuemax={3} aria-valuenow={step} aria-label={`Step ${step} of 3`}>{[1, 2, 3].map((n) => <i key={n} className={n <= step ? 'is-on' : ''} />)}</div>
            {error && <div className="bly-error" role="alert">{error}</div>}

            {step === 1 && (
                <>
                    <div className="fd-setup-hello">
                        <div className="fd-setup-face"><BaileyStill appearance={look} face /></div>
                        <h1>Hi, I’m Bailey. Let’s get me on your front desk.</h1>
                    </div>
                    <div className="fd-field">
                        <span className="fd-label is-big">How should I sound with your clients?</span>
                        <SoulCards value={keepSoul ? fd.soul.preset : soul} onPick={pickSoul} motion={!motionPaused} />
                    </div>
                    <div className="fd-bubble" aria-live="polite">
                        <small>Bailey</small>
                        {chatGreeting(soulNow, salonName || 'your salon')}
                    </div>
                    <p className="fd-hint">You can fine-tune it any time, on the soul page.</p>
                </>
            )}

            {step === 2 && (
                <>
                    <h1 className="fd-setup-title">Where should I answer?</h1>
                    <p className="fd-lead">Start with one. You can add the other any time.</p>
                    <div className="bly-card bly-list">
                        <div className="bly-row">
                            <span className="bly-tile" aria-hidden="true">💬</span>
                            <span className="bly-row-copy"><b>Chat on your booking page</b><small>Clients ask questions, get advice and book, any time</small></span>
                            <button type="button" className={`ds-toggle${chat ? ' ds-toggle--on' : ''}`} role="switch" aria-checked={chat} aria-label="Chat on your booking page" onClick={() => { hapticTick(); setChat(!chat); }}><span className="ds-toggle-knob" /></button>
                        </div>
                        <div className="bly-row">
                            <span className="bly-tile" aria-hidden="true">📞</span>
                            <span className="bly-row-copy">
                                <b>Answer your phone</b>
                                <small>{s.voiceNumber ? `${prettyNumber(s.voiceNumber)} is yours. Divert your salon line to it when you’re busy or closed.` : 'A local number of your own. Divert your salon line to it when you’re busy or closed.'}</small>
                            </span>
                            {s.voiceNumber ? (
                                <button type="button" className={`ds-toggle${phone ? ' ds-toggle--on' : ''}`} role="switch" aria-checked={phone} aria-label="Answer your phone" onClick={() => { hapticTick(); setPhone(!phone); }}><span className="ds-toggle-knob" /></button>
                            ) : (
                                <button type="button" className="bly-btn is-secondary fd-row-btn" disabled={!fd.ready} onClick={() => { hapticTick(); setPanel('phone'); }}>Set up</button>
                            )}
                            {phone && !!s.voiceNumber && <VoiceLine fd={fd} lead="Callers will hear " onChange={() => setPanel('voice')} />}
                        </div>
                    </div>
                    <div className="fd-price">{price}</div>
                </>
            )}

            {step === 3 && (
                <>
                    <h1 className="fd-setup-title">Anything I should know?</h1>
                    <div className="bly-card fd-knows">
                        {KNOWS.map((k) => <span key={k}><i><Icon.Tick size={12} /></i>{k}</span>)}
                    </div>
                    <div className="fd-field">
                        <span className="fd-label">Clients often ask these. Answer any you like, or skip.</span>
                        <div className="bly-card bly-list">
                            {COMMON_QUESTIONS.slice(0, 5).map((q) => {
                                const done = answered(q);
                                return (
                                    <button key={q} type="button" className="fd-answer" onClick={() => { hapticTick(); setPanel(done ? { answer: done } : { add: true, question: q }); }}>
                                        <span className="fd-answer-copy">
                                            <b>{q}</b>
                                            <small>{done ? done.answer : 'Not answered yet'}</small>
                                        </span>
                                        {done
                                            ? <span className="fd-done-tick" role="img" aria-label="Answered"><Icon.Tick size={12} /></span>
                                            : <span className="bly-chev" aria-hidden="true"><Icon.Chevron /></span>}
                                    </button>
                                );
                            })}
                        </div>
                        <p className="fd-hint">I’ll tell you when someone asks something I don’t know.</p>
                    </div>
                </>
            )}

            <div className="fd-step-bar">
                {step === 1
                    ? <button type="button" className="bly-btn is-ghost" onClick={notNow}>Not now</button>
                    : <button type="button" className="bly-btn is-ghost" onClick={() => { hapticTick(); setStep(step - 1); }}>Back</button>}
                {step < 3
                    ? <button type="button" className="bly-btn is-primary" onClick={() => { hapticTick(); setStep(step + 1); }}>Next</button>
                    : <button type="button" className="bly-btn is-primary" disabled={busy === 'live' || !canGo || !fd.ready} title={canGo ? undefined : 'Switch on chat or the phone first'} onClick={goLive}>{busy === 'live' ? 'Going live…' : 'Go live'}</button>}
            </div>

            {/* A number taken here is for answering: its switch comes on (nothing is live until Go live). */}
            {panel === 'voice' && <VoicePanel fd={fd} onClose={() => setPanel(null)} />}
            {panel === 'phone' && <PhonePanel fd={fd} onTook={() => setPhone(true)} onClose={() => setPanel(null)} />}
            {panel && (panel.add || panel.answer) && (
                <AnswerPanel
                    answer={panel.answer || null}
                    question={panel.question || ''}
                    answers={fd.answers}
                    onAdd={({ question, answer }) => fd.addAnswer(question, answer)}
                    onChange={(old, next) => fd.changeAnswer(old, next)}
                    onRemove={(old) => fd.removeAnswer(old._id)}
                    onClose={() => setPanel(null)}
                />
            )}
        </div>
    );
}
